import React, { forwardRef, useState, useMemo, useCallback, useEffect } from 'react';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import _ from 'lodash';
import { FieldTypes, parseSelectOptions } from 'utils/formUtils';
import GenericMappingForm from '../GenericMappingForm';
import UnmappedTag from 'components/UnmappedTag';
import { Form } from 'antd';

const NetSparkerMappingForm = forwardRef(function NetSparkerMappingForm({ disableProjectFields = false, ...props }, ref) {
  const [website, setWebsite] = useState([]);
  const [websiteList, setWebsiteList] = useState(()=>{
    if (props.record) {
      const { websiteId, websiteName } = props.record;

      return _.uniqBy(
        websiteId.split(',').map((websiteIdGroup, index) => ({
          id: websiteIdGroup.split('/')[0],
          name: websiteName.split(',')[index].split('/')[0],
          mappedStatus: false
        })),
        'id'
      );
    }
    return [];
  });
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [initialValues] = useState(() => {
    if (props.record) {
      return undefined;
    }
    return [
      {
        product: props.defaultProduct,
        subProduct: props.defaultSubProduct,
        subProductId: props.defaultSubProductId,
        environment: props.defaultEnvironment,
      },
    ];
  });


  const parseWebsiteListing = (websiteScanGroupListing) => {  
    let websiteMapping = {};
    let mappedStatusMapping = {};

    Object.keys(websiteScanGroupListing).forEach(_project => {
      websiteScanGroupListing[_project].forEach(project => {
        const { otherProperties: { websiteName, websiteId } = {}, mappedStatus} = project;
        websiteMapping = { ...websiteMapping, [websiteId]: websiteName };
        mappedStatusMapping = { ...mappedStatusMapping, [websiteId]: mappedStatus};
      }) 
    });
    setWebsiteList(_.map(websiteMapping, (name, id) => ({ id, name, mappedStatus: mappedStatusMapping[id] })));
  };

  useEffect(() => {
    setLoading(true);
    const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId });
    promise.then((response) => {
      const { projects } = response;
      const websiteScanGroups = _.groupBy(projects,'otherProperties.websiteId');
      parseWebsiteListing(websiteScanGroups);
      setWebsite(websiteScanGroups);
    }).catch(() => {
    }).finally(() => setLoading(false));

    return cancel;
  }, [props.loginConfigId,props.toolName]);


  const getFilteredProjectOptions = useCallback((websiteIds) => {
    if (_.isEmpty(website)) {
      return;
    }

    let filteredProjects = [];

    _.forEach(websiteIds, (websiteId) => {
      let websiteIdScanGroups = website[websiteId];
      websiteIdScanGroups = websiteIdScanGroups?.filter((scanGroup)=>{
        return scanGroup.id !== websiteId;
      })
      filteredProjects = [ ...filteredProjects, ...(websiteIdScanGroups || [])];
    });
    const projectOptions = filteredProjects?.map(project => ({ label: project.name, value:project.id }));
    return parseSelectOptions(projectOptions);

  }, [website]);

  const getExtraFieldsData = useCallback((config) => {
    const { websiteId, scanGroup } = config;

    let projects = [];

    _.forEach(websiteId, (websiteId) => {
      projects = [ ...projects, ...(website[websiteId] || [])];
    });
    let websiteNameList = [];
    let websiteUrlList = [];
    let websiteIdList = [];
    
    const websiteListMap = _.groupBy(projects, "otherProperties.websiteId");
    websiteId?.map((webId)=>{
      let ids = [];
      let names = [];
      let urls = []
      let url = websiteListMap[webId]?.at(0).otherProperties?.url;
      const websiteScanGroupMap = _.groupBy(websiteListMap[webId],"id");
      let websiteName = websiteScanGroupMap[webId]?.at(0)?.name;
      scanGroup?.map((scanGroupId)=>{
        const websiteScanGroup = websiteScanGroupMap[scanGroupId]?.at(0);
        if(websiteScanGroup){
          ids = [...ids, websiteScanGroup.id];
          names = [...names, websiteScanGroup.name];
          urls = [...urls, url];
        }
        return scanGroupId;
      })
      
      if(ids.length === 0){
        ids = [webId];
      }
      if(names.length === 0){
        names = [websiteName];
      }
      if(urls.length === 0){
        urls = [url];
      }

      websiteNameList.push(names?.filter(Boolean)?.join(","));
      websiteIdList.push(ids?.filter(Boolean)?.join(","));
      websiteUrlList.push(urls?.filter(Boolean)?.join(","));

      return webId;
    });

    return ({
      "websiteName" : websiteNameList.join(","),
      "websiteId" : websiteIdList.join(","),
      "websiteUrl" : websiteUrlList.join(","),
      //send the property received in config
      ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
    });
  },[website]);

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { websiteId } = config;

    const websiteIdsSet = new Set();
    const scanGroupSet = new Set();

    websiteId?.split(",").forEach(item => {
      const [id, subId] = item.split('/');
      websiteIdsSet.add(id);
      if (subId !== undefined) {
        scanGroupSet.add(item);
      }
    });
    return {websiteId: [...websiteIdsSet], scanGroup: [...scanGroupSet]};
  }, []);

  const unmappedBadge = (data) =>{
    const mappedStatus = data.map(item => {
      return {
        label: (
          <div>
            <span className='m-r-m'>{item.name}</span> 
            {item.mappedStatus !== true && <UnmappedTag />}
          </div>
        ),
        value: item.id,
        filterLable: item.name,
      }
    })
    return mappedStatus;
  }

  const changeProject = useCallback((value, field) => {
    const { name } = field;
    const configurations = form.getFieldValue("configurations");
    let updatedConfiguration = [
      ...configurations,
    ];
    updatedConfiguration[name] = {
      ...updatedConfiguration[name],
      scanGroup: undefined,
    }
    form.setFieldsValue({ configurations: updatedConfiguration });
  }, [form]);

  const isProjectFieldDisabled = useCallback((fieldKey, form) => {
    const fieldConfiguration = form.getFieldValue("configurations")[fieldKey];
    return (!fieldConfiguration || !fieldConfiguration.websiteId);
  }, []);

  const extraFields = useMemo(() => ([
    {
      name: 'websiteId',
      colSpan: '12',
      type: FieldTypes.MULTI_SELECT,
      label: 'Website Name',
      required:true,
      options: unmappedBadge(websiteList),
      disabled: disableProjectFields,
      props: {
        mode: "multiple",
        autoFocus: !disableProjectFields,
        onChange: changeProject,
        optionFilterProp: "filterLable"
      }
    },
    {
      name: 'scanGroup',
      colSpan: '12',
      type: FieldTypes.MULTI_SELECT,
      label: 'Website Scan Group',
      required: false,
      propsGenerator: (fieldKey) => ({ disabled: isProjectFieldDisabled(fieldKey, form) || disableProjectFields }),
      props: {
        getFormListOptions: (fieldKey) => {
          const fieldConfiguration = form.getFieldValue("configurations")[fieldKey];
          return getFilteredProjectOptions(fieldConfiguration?.websiteId);
        },
      },
    }
  ]), [disableProjectFields, form, getFilteredProjectOptions, websiteList, changeProject, isProjectFieldDisabled]);

  return (
    <GenericMappingForm
      form={form}
      ref={ref}
      toolLoading={loading}
      getExtraFieldsData={getExtraFieldsData}
      extraFields={extraFields}
      formDefaultValues={initialValues}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      {...props}
    />
  )
})

export default NetSparkerMappingForm;
